import React, { useEffect } from "react";
import { Box, Link, Typography } from "@mui/material";
import {
  APIProvider,
  Map,
  Marker,
  InfoWindow,
  useMap,
} from "@vis.gl/react-google-maps";
import { useTranslation } from "react-i18next";
import { getGoogleMapsUrl, getGooglePlacePhotoUrl } from "../../utils/googleMapsLink";
import { estimateWalkMinutes } from "../../utils/geo";
import PlaygroundRating from "./PlaygroundRating";

const COPENHAGEN_CENTER = { lat: 55.6761, lng: 12.5683 };

// Hide default POI markers/labels (restaurants, hospitals, shops, etc.) so
// only our own playground markers stand out on the map.
// Note: inline styles only apply when the map falls back to raster rendering;
// vector maps (needed for two-finger rotation) require cloud-based styling
// via a Map ID to hide POIs.
const MAP_STYLES = [
  { featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] },
  { featureType: "poi", elementType: "geometry", stylers: [{ visibility: "off" }] },
  { featureType: "transit", elementType: "labels.icon", stylers: [{ visibility: "off" }] },
];

// Pans/zooms the map to the selected playground whenever it changes (e.g.
// picked from the list). Must live inside <Map> to access the map instance.
const PanToSelected = ({ selected }) => {
  const map = useMap();

  useEffect(() => {
    if (!map || !selected) return;
    map.panTo({ lat: selected.lat, lng: selected.lng });
    if (map.getZoom() < 15) map.setZoom(15);
  }, [map, selected]);

  return null;
};

const PlaygroundMap = ({ playgrounds, userPosition, selectedId, onSelect }) => {
  const { t } = useTranslation();

  const selected = playgrounds.find((p) => p.id === selectedId) || null;
  const center = userPosition || COPENHAGEN_CENTER;

  return (
    <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}>
      <Box sx={{ width: "100%", height: "70vh" }}>
        <Map
          defaultCenter={center}
          defaultZoom={13}
          gestureHandling="greedy"
          disableDefaultUI={false}
          renderingType="VECTOR"
          headingInteractionEnabled
          tiltInteractionEnabled
          styles={MAP_STYLES}
          style={{ width: "100%", height: "100%" }}
        >
          <PanToSelected selected={selected} />

          {playgrounds.map((playground) => (
            <Marker
              key={playground.id}
              position={{ lat: playground.lat, lng: playground.lng }}
              onClick={() => onSelect(playground.id)}
              zIndex={playground.id === selectedId ? 999 : undefined}
              icon={
                playground.id === selectedId
                  ? "https://maps.google.com/mapfiles/ms/icons/orange-dot.png"
                  : undefined
              }
            />
          ))}

          {userPosition && (
            <Marker
              position={userPosition}
              icon="https://maps.google.com/mapfiles/ms/icons/blue-dot.png"
            />
          )}

          {selected && (
            <InfoWindow
              position={{ lat: selected.lat, lng: selected.lng }}
              onCloseClick={() => onSelect(null)}
            >
              <Box sx={{ maxWidth: 220 }}>
                {selected.photoReference && (
                  <Box
                    component="img"
                    src={getGooglePlacePhotoUrl(selected.photoReference)}
                    alt={selected.name}
                    sx={{
                      width: "100%",
                      height: 120,
                      objectFit: "cover",
                      borderRadius: 1,
                      mb: 1,
                    }}
                  />
                )}
                <Typography variant="subtitle2" fontWeight={600}>
                  {selected.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selected.address}
                </Typography>
                <PlaygroundRating
                  rating={selected.rating}
                  userRatingsTotal={selected.userRatingsTotal}
                />
                {typeof selected.distanceKm === "number" && (
                  <Typography variant="body2" color="text.secondary">
                    {selected.distanceKm.toFixed(1)} {t("playgrounds.km_unit")}
                    {" · "}
                    {t("playgrounds.walk_time", {
                      minutes: estimateWalkMinutes(selected.distanceKm),
                    })}
                  </Typography>
                )}
                <Link
                  href={getGoogleMapsUrl(selected)}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="body2"
                  sx={{ display: "inline-block", mt: 0.5 }}
                >
                  {t("playgrounds.view_on_google_maps")}
                </Link>
              </Box>
            </InfoWindow>
          )}
        </Map>
      </Box>
    </APIProvider>
  );
};

export default PlaygroundMap;
